import {
  getContainerClientRegisteredInfo,
  getHistoryCollection,
  getHistoryCycleContainer,
} from "../../../services/container";
import { resolveActivityFluxLabel } from "./collectionRowUtils";

export const resolveFluxFromCycleAndRegistration = (
  item,
  historyCycle,
  wasteCategoryJson,
) =>
  resolveActivityFluxLabel({
    wasteCategoryJson,
    containerWasteName: historyCycle?.[0]?.wasteName ?? item?.wasteName ?? "",
  });

const resolveSuperContainerIdForExport = (measurementId, superContainers) => {
  let id = measurementId;
  if (Array.isArray(superContainers) && superContainers.length > 0) {
    for (const superContainer of superContainers) {
      if (superContainer.containerObjectIdList?.includes(id)) {
        return superContainer._id;
      }
    }
  }
  return id;
};

/** Same flux resolution as useCollectionExport fetchCycleData (Activity CSV). */
const fetchCollectionExportFluxContext = async (item, superContainers) => {
  if (!item?._time || !item?._measurement) {
    return {
      flux: item?.wasteName ?? "",
      historyCycle: [],
    };
  }

  try {
    const clientContainerId = resolveSuperContainerIdForExport(
      item._measurement,
      superContainers,
    );

    const responseHistoryCycle = await getHistoryCycleContainer(
      {
        currentCycle: false,
        getLevelLastRotation: false,
        containerCollectionDate: item._time,
      },
      item._measurement,
    );

    const cycleData =
      responseHistoryCycle?.status === 200 ? responseHistoryCycle.data : {};
    const historyCycle = cycleData?.historyCycle || [];
    const dropOff = historyCycle[historyCycle.length - 1]?._time;

    let wasteCategoryJson;
    if (dropOff) {
      const clientRegisteredInfo = await getContainerClientRegisteredInfo(
        {
          start: new Date(new Date(dropOff).getTime() - 1).toISOString(),
          stop: new Date(new Date(item._time).getTime() + 1).toISOString(),
        },
        clientContainerId,
      );

      wasteCategoryJson = clientRegisteredInfo?.data?.waste_category;
    }

    const flux = resolveFluxFromCycleAndRegistration(
      item,
      historyCycle,
      wasteCategoryJson,
    );

    return { flux, historyCycle };
  } catch {
    return {
      flux: item?.wasteName ?? "",
      historyCycle: [],
    };
  }
};

const normalizeImageUrlKey = (url) => {
  if (!url || typeof url !== "string") return "";
  return url.split("?")[0];
};

const collectionItemKey = (item) =>
  item?._measurement && item?._time ? `${item._measurement}_${item._time}` : "";

/**
 * Precomputes flux labels like Activity → export CSV, keyed by collection
 * pickup and by cycle image URLs.
 */
export const buildCollectionFluxLookup = async ({
  periodHistoryStart,
  periodHistoryStop,
  keywordFilter,
  superContainers,
}) => {
  const keywordValue =
    keywordFilter && keywordFilter.value
      ? keywordFilter.value
      : Array.isArray(keywordFilter)
        ? keywordFilter
        : [];

  const response = await getHistoryCollection({
    periodStart: periodHistoryStart,
    periodStop: periodHistoryStop,
    keywordFilter: JSON.stringify(keywordValue),
    keepFirst: true,
  });

  const byCollectionKey = new Map();
  const byImageUrl = new Map();

  const resolveForHistoryItem = (historyItem) => {
    const imageKey = normalizeImageUrlKey(historyItem?.image_url);
    if (imageKey && byImageUrl.has(imageKey)) {
      return byImageUrl.get(imageKey);
    }
    const key = collectionItemKey(historyItem);
    if (key && byCollectionKey.has(key)) {
      return byCollectionKey.get(key);
    }
    return null;
  };

  if (response.status !== 200) {
    return { byCollectionKey, byImageUrl, resolveForHistoryItem };
  }

  const collectionItems = response.data?.historyCollection || [];

  for (const collectionItem of collectionItems) {
    const { flux, historyCycle } = await fetchCollectionExportFluxContext(
      collectionItem,
      superContainers,
    );

    const key = collectionItemKey(collectionItem);
    if (key) {
      byCollectionKey.set(key, flux);
    }

    for (const entry of historyCycle) {
      const imageKey = normalizeImageUrlKey(entry.image_url);
      if (imageKey) {
        byImageUrl.set(imageKey, flux);
      }
      if (entry._time && collectionItem._measurement) {
        byCollectionKey.set(
          `${collectionItem._measurement}_${entry._time}`,
          flux,
        );
      }
    }
  }

  return { byCollectionKey, byImageUrl, resolveForHistoryItem };
};
